import { clsx } from "clsx";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useClerk } from "@clerk/clerk-react";
import {
  Activity,
  AlertOctagon,
  Briefcase,
  Building2,
  ChevronDown,
  ChevronRight,
  ClipboardList,
  Cpu,
  FileText,
  GitCommit,
  HeartPulse,
  Library,
  LogOut,
  MessageSquare,
  MonitorCog,
  Package,
  RefreshCw,
  Server,
  Settings,
  ShieldCheck,
  Sparkles,
  Trash2,
  Users,
  Wrench,
  Zap,
  type LucideIcon,
} from "lucide-react";
import { Link, useLocation, useSearchParams } from "react-router-dom";
import { clearAuthCache, invalidateAuth } from "@/lib/lexiun-jwt";
import { resolveAppUrl } from "@/lib/url";
import { SPECIALISTS_PATH } from "@/lib/routes";

const clerkPk = import.meta.env.VITE_CLERK_PUBLISHABLE_KEY as string | undefined;

interface NavItem {
  to: string;
  icon: LucideIcon;
  label: string;
  /** Optional active-check override — defaults to `pathname.startsWith(to)`.
   *  Used to distinguish Organizations vs Recycle Bin, which share `/orgs`. */
  isActive?: (pathname: string, searchParams: URLSearchParams) => boolean;
  /** Optional stable testid (preserves prior `sidebar-recycle-bin` etc.). */
  testid?: string;
}

interface NavGroup {
  id: string;
  title: string;
  icon: LucideIcon;
  items: readonly NavItem[];
}

/**
 * Sidebar navigation — 5 collapsible groups, max 4 items per group.
 *
 * Group order is by usage frequency for the typical platform-admin flow:
 *   Tenants  →  Execution  →  Diagnostics  →  AI Config  →  Infrastructure
 *
 * Expansion state persists per-operator in localStorage. The group
 * containing the currently-active route is force-expanded regardless of
 * stored state (so a deep-link to a "collapsed" route still shows the
 * containing group). Default-expanded on first load.
 *
 * Route paths are unchanged — every existing deep-link / bookmark
 * continues to work. Reorg is presentation-only.
 */
const navGroups: readonly NavGroup[] = [
  {
    id: "tenants",
    title: "Tenants",
    icon: Users,
    items: [
      {
        to: "/orgs",
        icon: Building2,
        label: "Organizations",
        // Distinguish from Recycle Bin (same /orgs path, different query).
        isActive: (p, sp) =>
          p.startsWith("/orgs") && sp.get("status") !== "DELETED",
      },
      { to: "/cases", icon: Briefcase, label: "Cases" },
      { to: "/feedback", icon: MessageSquare, label: "Feedback" },
      {
        to: "/orgs?status=DELETED",
        icon: Trash2,
        label: "Recycle Bin",
        testid: "sidebar-recycle-bin",
        isActive: (p, sp) =>
          p.startsWith("/orgs") && sp.get("status") === "DELETED",
      },
    ],
  },
  {
    id: "execution",
    title: "Execution",
    icon: Zap,
    items: [
      { to: "/execution-ledger", icon: Zap, label: "Executions" },
      { to: "/checkpoints", icon: GitCommit, label: "Checkpoints" },
      { to: "/tasks", icon: ClipboardList, label: "Tasks" },
      { to: "/supervisor", icon: ShieldCheck, label: "Supervisor" },
      { to: "/activity", icon: Sparkles, label: "Activity" },
    ],
  },
  {
    id: "diagnostics",
    title: "Diagnostics",
    icon: AlertOctagon,
    items: [
      { to: "/errors", icon: AlertOctagon, label: "Errors" },
      { to: "/operational-logs", icon: FileText, label: "Op Logs" },
      { to: "/observability/llm-calls", icon: Activity, label: "LLM Calls" },
      { to: "/health", icon: HeartPulse, label: "Health" },
    ],
  },
  {
    id: "ai-config",
    title: "AI Config",
    icon: Cpu,
    items: [
      { to: "/llm-providers", icon: Cpu, label: "LLM Providers" },
      { to: "/platform-prompts", icon: Library, label: "Prompts" },
      { to: SPECIALISTS_PATH, icon: Users, label: "Specialists" },
    ],
  },
  {
    id: "infrastructure",
    title: "Infrastructure",
    icon: Wrench,
    items: [
      { to: "/runtimes", icon: Server, label: "Runtimes" },
      {
        to: "/runtime-parameters",
        icon: MonitorCog,
        label: "Runtime Parameters",
      },
      { to: "/platform/extensions", icon: Package, label: "Lextools" },
      { to: "/platform/settings", icon: Settings, label: "Platform Settings" },
    ],
  },
] as const;

const STORAGE_KEY = "lexportal.sidebar.groups";

/** Load the persisted "explicitly collapsed" set. Items NOT in the set are
 *  treated as expanded (default-expanded for first-time operators). */
function loadCollapsedSet(): Set<string> {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return new Set();
    const arr = JSON.parse(raw);
    if (Array.isArray(arr)) return new Set(arr.filter((x) => typeof x === "string"));
  } catch {
    // Corrupt JSON or storage disabled — fall back to all-expanded.
  }
  return new Set();
}

function saveCollapsedSet(set: Set<string>): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(Array.from(set)));
  } catch {
    // Storage disabled (private mode etc.) — silently no-op.
  }
}

const footerBtnCls =
  "flex w-full items-center gap-3 rounded-md px-3 py-2 text-sm font-medium text-muted transition-colors hover:bg-accent hover:text-foreground";

/**
 * Prod sign-out: end the Clerk session AND drop the cached Lexiun JWT
 * (the REST bearer is independent of the Clerk session — clearing only
 * one would leave a stale token behind), then land on the sign-in page.
 * Only mounted when `clerkPk` is set, so `useClerk()` is always inside
 * <ClerkProvider> (App.tsx renders Sidebar under PlainProviderStack in
 * dev where there is no ClerkProvider — hence the build-constant split
 * rather than a conditional hook call).
 */
function ClerkSignOutButton() {
  const { signOut } = useClerk();
  const onClick = useCallback(() => {
    // `clearAuthCache()` (not `invalidateAuth()`): prod never has a
    // localStorage seed (the dev auto-seed endpoint 403s in prod), so
    // there is no seed to clear — and `invalidateAuth()` would fire
    // notifyAuth("expired"), flashing a wrong "Session expired" banner
    // during the async signOut() before the redirect lands. The dev
    // path (DevResetButton) DOES use invalidateAuth() — that is where
    // a seed actually exists.
    clearAuthCache();
    void signOut().finally(() => {
      window.location.assign(resolveAppUrl("/sign-in"));
    });
  }, [signOut]);
  return (
    <button
      type="button"
      onClick={onClick}
      className={footerBtnCls}
      data-testid="sidebar-signout"
    >
      <LogOut className="h-4 w-4 shrink-0" />
      Sign out
    </button>
  );
}

/**
 * Dev has no auth wall — `getLexiunToken()` auto-mints a PLATFORM_ADMIN
 * token, so a true "sign out" isn't possible (the next call re-seeds).
 * The honest, useful dev action is a session RESET: drop the cached +
 * localStorage seed token and reload so a fresh one is minted. Mirrors
 * AuthRecoveryBanner's dev "Reload to reconnect" path.
 */
function DevResetButton() {
  const onClick = useCallback(() => {
    invalidateAuth();
    window.location.reload();
  }, []);
  return (
    <button
      type="button"
      onClick={onClick}
      className={footerBtnCls}
      data-testid="sidebar-signout"
      title="Dev has no auth wall — this clears the cached token and re-mints a fresh dev session on reload."
    >
      <RefreshCw className="h-4 w-4 shrink-0" />
      Reset session
    </button>
  );
}

export function Sidebar() {
  const { pathname } = useLocation();
  const [searchParams] = useSearchParams();
  const [collapsed, setCollapsed] = useState<Set<string>>(loadCollapsedSet);

  // Determine which group contains the currently-active route — that
  // group is force-expanded even if the operator previously collapsed it.
  const activeGroupId = useMemo(() => {
    for (const g of navGroups) {
      for (const it of g.items) {
        const active = it.isActive
          ? it.isActive(pathname, searchParams)
          : pathname.startsWith(it.to);
        if (active) return g.id;
      }
    }
    return null;
  }, [pathname, searchParams]);

  useEffect(() => {
    saveCollapsedSet(collapsed);
  }, [collapsed]);

  const toggleGroup = useCallback((id: string) => {
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  return (
    <aside className="flex w-56 flex-col border-r border-border bg-panel/80 backdrop-blur">
      {/* Brand — also the Home link. Home = Organizations (the platform-admin
          landing). Standard logo=home convention. */}
      <Link
        to="/orgs"
        className="group flex items-center gap-2 border-b border-border px-4 py-4 transition-colors hover:bg-accent/40"
        data-testid="sidebar-home"
        aria-label="LexPortal home"
      >
        <MonitorCog className="h-5 w-5 text-primary" />
        <span className="text-sm font-semibold tracking-tight transition-colors group-hover:text-primary">
          LexPortal
        </span>
      </Link>

      <nav className="flex-1 space-y-2 overflow-y-auto px-2 py-3">
        {navGroups.map((g) => {
          // A group containing the active route stays expanded regardless
          // of stored collapsed state — operator never lands on a route
          // whose entry isn't visible in the sidebar.
          const isExpanded = activeGroupId === g.id || !collapsed.has(g.id);
          return (
            <NavGroupSection
              key={g.id}
              group={g}
              expanded={isExpanded}
              onToggle={() => toggleGroup(g.id)}
              pathname={pathname}
              searchParams={searchParams}
            />
          );
        })}
      </nav>

      {/* Session control */}
      <div className="border-t border-border px-2 py-3">
        {clerkPk ? <ClerkSignOutButton /> : <DevResetButton />}
      </div>
    </aside>
  );
}

interface NavGroupSectionProps {
  group: NavGroup;
  expanded: boolean;
  onToggle: () => void;
  pathname: string;
  searchParams: URLSearchParams;
}

function NavGroupSection({
  group,
  expanded,
  onToggle,
  pathname,
  searchParams,
}: NavGroupSectionProps) {
  const HeaderIcon = group.icon;
  return (
    <div className="space-y-1" data-testid={`sidebar-group-${group.id}`}>
      <button
        type="button"
        onClick={onToggle}
        className="flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-xs font-semibold uppercase tracking-wider text-faint transition-colors hover:bg-accent/40 hover:text-muted"
        aria-expanded={expanded}
        data-testid={`sidebar-group-toggle-${group.id}`}
      >
        {expanded ? (
          <ChevronDown className="h-3 w-3 shrink-0" />
        ) : (
          <ChevronRight className="h-3 w-3 shrink-0" />
        )}
        <HeaderIcon className="h-3.5 w-3.5 shrink-0" />
        <span>{group.title}</span>
      </button>
      {expanded &&
        group.items.map((it) => {
          const ItemIcon = it.icon;
          const active = it.isActive
            ? it.isActive(pathname, searchParams)
            : pathname.startsWith(it.to);
          return (
            <Link
              key={it.to}
              to={it.to}
              data-testid={it.testid}
              className={clsx(
                "flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition-colors",
                active
                  ? "bg-signal-soft text-primary shadow-[inset_2px_0_0_0_var(--color-primary)]"
                  : "text-muted hover:bg-accent hover:text-foreground",
              )}
            >
              <ItemIcon className="h-4 w-4 shrink-0" />
              {it.label}
            </Link>
          );
        })}
    </div>
  );
}
